import {
  type Channel,
  type ChannelKind,
  type CliReportView,
  connectedLine,
  Ingest,
  type PolicyPreset,
  PRESETS,
} from "@pagerless/schema";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { Project, ProjectStatus } from "../api";
import { ScreenHead } from "../components/ScreenHead";
import { ErrorNote, Loading } from "../components/Shell";
import { Wordmark } from "../components/Wordmark";
import { useDeps, useResource } from "../context";
import { passkeyHere, savePasskeyHere } from "../lib/devicePasskey";
import {
  cliSetupPager,
  connectPager,
  LOADING_PAGER,
  type PagerView,
  peoplePager,
  setupHelperPager,
} from "../lib/pager";

function SetupFrame({
  step,
  title,
  pager,
  children,
}: {
  step: 1 | 2 | 3;
  title: string;
  pager: PagerView;
  children: ReactNode;
}) {
  return (
    <>
      <title>{`${title} · Setup · Pagerless`}</title>
      <div className="setup">
        <div className="win">
          <div className="setup-bar">
            <Link to="/" className="logo" style={{ fontSize: 24 }}>
              <Wordmark />
            </Link>
            <ol className="prog" aria-hidden="true">
              {[1, 2, 3].map((i) => (
                <li key={i} className={i <= step ? "on" : undefined} />
              ))}
            </ol>
            <span className="note" style={{ color: "var(--ink2)" }}>
              Step {step} of 3
            </span>
          </div>
          <main id="main" className="pad" style={{ padding: "var(--s5)" }}>
            <ScreenHead pager={pager} title={title} live />
            {children}
          </main>
        </div>
      </div>
    </>
  );
}

/**
 * Where an app should send its events when this server is not the package's built-in address
 * (Ingest.baseUrl): any self-hosted or workers.dev deploy. Null when the default already points here.
 * Regression (prod, 22 Oct): setup never mentioned it, so a connected app sent to a host that didn't exist.
 */
export function ingestEndpoint(origin: string): string | null {
  const host = new URL(origin).host;
  const product = new URL(Ingest.baseUrl).host.replace(/^[^.]+\./, ""); // events.pagerless.dev → pagerless.dev
  return host === product || host.endsWith(`.${product}`) ? null : `${origin}/ingest${Ingest.eventsPath}`;
}

/**
 * What to tell your AI (or type yourself): one command that signs in, finds every service and asks
 * before changing anything. No key in the prompt: the command puts keys where they belong.
 */
export function connectCommand(origin: string): string {
  return `npx pagerless connect${ingestEndpoint(origin) ? ` --api ${origin}` : ""}`;
}

export function aiPrompt(origin: string): string {
  return `Run ${connectCommand(origin)} in this repo and follow what it asks.`;
}

/** The environment lines to paste into the app's settings. */
export function envLines(key: string, endpoint: string | null): string {
  return `PAGERLESS_KEY=${key}${endpoint ? `\nPAGERLESS_ENDPOINT=${endpoint}` : ""}`;
}

/** Shows the key masked like the mock ("pl_live_8f2c…a91") until the person copies it. */
function maskKey(key: string): string {
  return key.length > 16 ? `${key.slice(0, 12)}…${key.slice(-3)}` : key;
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const { copy } = useDeps();
  const [done, setDone] = useState<"idle" | "ok" | "fail">("idle");
  return (
    <>
      <button
        type="button"
        className="btn sm"
        onClick={() =>
          copy(text).then(
            () => setDone("ok"),
            () => setDone("fail"),
          )
        }
      >
        {done === "ok" ? "Copied" : label}
      </button>
      <span role="status" className="note" style={{ marginLeft: 8 }}>
        {done === "ok"
          ? "Copied. Paste it into your AI."
          : done === "fail"
            ? "Couldn't copy. Select the text instead."
            : ""}
      </span>
    </>
  );
}

/** Polls until the app sends its first event. Stops on unmount and once online. */
/**
 * "Found Maya's Shop. Nothing to set up." The app's display name, never its service slug (D3); what we can
 * see inside it (checkout, payments…) when the api says, without repeating the slug.
 */
export function foundLine(name: string | null, status: ProjectStatus | null): string {
  const parts = (status?.found ?? []).filter((f) => f !== status?.service);
  return `Found ${name ?? "your app"}${parts.length > 0 ? `: ${parts.join(", ")}` : ""}. Nothing to set up.`;
}

/** What `npx pagerless connect` set up, polled until the app is online. */
function useCliReport(): CliReportView | null {
  const { api, pollMs } = useDeps();
  const [report, setReport] = useState<CliReportView | null>(null);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const r = await api.cliReport();
        if (live) setReport(r);
      } catch {
        // The first-event poll already shows connection trouble; this one stays quiet.
      }
      if (live) timer = setTimeout(tick, pollMs * 4);
    };
    void tick();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [api, pollMs]);
  return report;
}

function useFirstEvent(projectId: string | null): { status: ProjectStatus | null; error: Error | null } {
  const { api, pollMs } = useDeps();
  const [status, setStatus] = useState<ProjectStatus | null>(null);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!projectId) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const s = await api.projectStatus(projectId);
        if (!live) return;
        setStatus(s);
        setError(null);
        if (s.online) return;
      } catch (e) {
        if (!live) return;
        // Keep polling through blips, but say so: a silent spinner would hide a real outage.
        setError(e instanceof Error ? e : new Error(String(e)));
      }
      timer = setTimeout(tick, pollMs);
    };
    void tick();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [api, pollMs, projectId]);
  return { status, error };
}

export function SetupConnect() {
  const { api, redirect } = useDeps();
  const navigate = useNavigate();
  const conn = useResource((a) => a.connect());
  const [how, setHow] = useState<"ai" | "line" | "host">("ai");
  const [otherWay, setOtherWay] = useState(false);
  const project: Project | null = conn.status === "ok" ? conn.data.project : null;
  const cli = useCliReport();
  // Watch the app the terminal connected, when it reported: an account's first app can be another one
  // (proofsource sent heartbeats while this screen watched "My app" and said WAITING FOR DEPLOY).
  const { status, error } = useFirstEvent(cli?.projectId ?? project?.id ?? null);
  const online = status?.online === true;

  if (conn.status === "loading")
    return (
      <SetupFrame step={1} title="Connect your app" pager={LOADING_PAGER}>
        <Loading what="Making your key" />
      </SetupFrame>
    );
  if (conn.status === "error")
    return (
      <SetupFrame step={1} title="Connect your app" pager={connectPager({ online: false, name: null })}>
        <ErrorNote error={conn.error} retry={conn.reload} />
      </SetupFrame>
    );
  const key = conn.data.key;
  const endpoint = ingestEndpoint(window.location.origin);

  const choices: Array<{ id: typeof how; title: string; body: ReactNode }> = [
    {
      id: "ai",
      title: "Ask your AI to do it",
      body: (
        <>
          <p>Paste into Claude, Cursor or your terminal. It finds every service and asks before changing anything.</p>
          <pre className="code">{aiPrompt(window.location.origin)}</pre>
          <CopyButton text={aiPrompt(window.location.origin)} />
        </>
      ),
    },
    {
      id: "line",
      title: "Paste one line",
      body: (
        <>
          <p>Do it yourself.</p>
          <pre className="code">{`npm i pagerless\nimport 'pagerless/auto'`}</pre>
          <p className="note" style={{ margin: "0 0 8px" }}>
            Then set {endpoint ? "these in your app's settings" : <code>PAGERLESS_KEY</code>}
            {endpoint ? ":" : " to your key."}
          </p>
          {endpoint && <pre className="code">{envLines(maskKey(key), endpoint)}</pre>}
          <CopyButton text={endpoint ? envLines(key, endpoint) : key} label={endpoint ? "Copy settings" : "Copy key"} />
        </>
      ),
    },
    {
      id: "host",
      title: "One click",
      body: (
        <>
          <p>Apps on Vercel or Railway.</p>
          <div className="btn-row" style={{ margin: 0 }}>
            {(["vercel", "railway"] as const).map((h) => (
              <button
                key={h}
                type="button"
                className="btn alt sm"
                onClick={() => project && redirect(api.hostConnectUrl(h, project.id))}
              >
                Connect {h === "vercel" ? "Vercel" : "Railway"}
              </button>
            ))}
          </div>
        </>
      ),
    },
  ];

  return (
    <SetupFrame
      step={1}
      title={
        online
          ? `${project?.name ?? "Your app"} is connected`
          : cli
            ? `${cli.repo ?? "Your app"} is set up`
            : "Connect your app"
      }
      pager={
        !online && cli ? cliSetupPager(cli) : connectPager({ online, name: project?.name ?? status?.service ?? null })
      }
    >
      {/* Once the app is found, the ways to connect are done with: only the confirmation and Next remain (bug 10). */}
      {/* After `npx pagerless connect` the install choices are done with: say what was set up and what's next. */}
      {!online && cli && !otherWay && (
        <div className="box">
          <b>Set up from your terminal</b>
          <p className="muted" style={{ margin: "var(--s2) 0 0" }}>
            {connectedLine(cli)}
            {cli.repo ? ` in ${cli.repo}` : ""}. Merge the pull request and deploy; this page lights up when the first
            service checks in.
          </p>
          <button type="button" className="link-btn" onClick={() => setOtherWay(true)}>
            Connect another app
          </button>
        </div>
      )}
      {!online && (!cli || otherWay) && (
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="sr-only">How do you want to connect?</legend>
          <div className="picks">
            {choices.map((c) => (
              // Each card is a region the person can work in; clicking anywhere marks it as their choice.
              // biome-ignore lint/a11y/noStaticElementInteractions: the inner buttons carry keyboard access.
              // biome-ignore lint/a11y/useKeyWithClickEvents: same as above.
              <div key={c.id} className={`box pick${how === c.id ? " on" : ""}`} onClick={() => setHow(c.id)}>
                <b>{c.title}</b>
                {c.body}
              </div>
            ))}
          </div>
        </fieldset>
      )}

      <div className="live">
        <div>
          {online ? (
            <p className="muted" style={{ margin: 0 }}>
              {foundLine(project?.name ?? null, status)}
            </p>
          ) : (
            <>
              {cli ? (
                <p className="muted" style={{ margin: 0 }}>
                  Waiting for the first check-in. You can set up who to wake meanwhile.
                </p>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  Updates by itself when your app sends its first signal.
                </p>
              )}
              {error && <ErrorNote error={error} />}
            </>
          )}
        </div>
        <div className="btn-row" style={{ margin: 0 }}>
          {!online && !cli && (
            <button type="button" className="btn alt" onClick={() => navigate("/setup/people")}>
              Skip for now
            </button>
          )}
          <button type="button" className="btn" disabled={!online && !cli} onClick={() => navigate("/setup/people")}>
            Next
          </button>
        </div>
      </div>
    </SetupFrame>
  );
}

const CHANNEL_CHOICES: Array<{ kinds: ChannelKind[]; title: string; detail: string }> = [
  { kinds: ["push_ios", "push_android"], title: "Pagerless phone app", detail: "Rings even on silent" },
  { kinds: ["call"], title: "Phone call", detail: "If no tap in 5 min · your Twilio" },
  { kinds: ["slack", "telegram"], title: "Slack or Telegram", detail: "For things that can wait" },
];

/** What a channel gets when turned on: calls only wake, the phone app wakes and tells, the rest tell. */
export function defaultLevels(kind: ChannelKind): Channel["levels"] {
  return kind === "call" ? ["wake"] : kind.startsWith("push") ? ["wake", "tell"] : ["tell"];
}

function channelOn(c: Channel): boolean {
  return c.levels.length > 0;
}

export function SetupPeople() {
  const navigate = useNavigate();
  const people = useResource((a) => a.people());
  return (
    <SetupFrame
      step={2}
      title="Who should we wake?"
      pager={people.status === "ok" ? peoplePager(people.data) : LOADING_PAGER}
    >
      <PeopleAndChannels />
      <div className="btn-row" style={{ marginTop: "var(--s5)" }}>
        <button type="button" className="btn alt" onClick={() => navigate("/setup")}>
          Back
        </button>
        <button type="button" className="btn" onClick={() => navigate("/setup/helper")}>
          Next
        </button>
      </div>
    </SetupFrame>
  );
}

/** People in call order beside how we reach them: two columns, same headings, same top edge. */
export function PeopleAndChannels() {
  return (
    <div className="two">
      <section aria-labelledby="who-h">
        <h2 id="who-h" className="h3" style={{ marginTop: 0 }}>
          In this order
        </h2>
        <PeopleList />
      </section>
      <section aria-labelledby="how-h">
        <h2 id="how-h" className="h3" style={{ marginTop: 0 }}>
          How we reach you
        </h2>
        <ChannelList />
      </section>
    </div>
  );
}

export function PeopleList() {
  const { api, passkey } = useDeps();
  const people = useResource((a) => a.people());
  const me = useResource((a) => a.me());
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<Error | null>(null);
  const [savingKey, setSavingKey] = useState(false);
  // Re-read after saving, so the row updates without a reload.
  const [savedHere, setSavedHere] = useState(0);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.invite(name.trim(), email.trim());
      setAdding(false);
      setName("");
      setEmail("");
      people.reload();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
    }
  }

  async function savePasskey() {
    setSavingKey(true);
    setErr(null);
    try {
      if (me.status !== "ok") return;
      await savePasskeyHere(api, passkey, me.data.person.id);
      setSavedHere((n) => n + 1);
      me.reload();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
    } finally {
      setSavingKey(false);
    }
  }

  if (people.status === "loading") return <Loading what="Loading people" />;
  if (people.status === "error") return <ErrorNote error={people.error} retry={people.reload} />;
  const sorted = [...people.data].sort((a, b) => a.order - b.order);
  const myId = me.status === "ok" ? me.data.person.id : null;
  const first = sorted[0];
  // The server only knows "has a passkey somewhere"; only this browser knows if one is here (bug e).
  const here = myId !== null && savedHere >= 0 && passkeyHere(myId);

  return (
    <div>
      <ul className="box list">
        {sorted.map((p, i) => (
          <li key={p.id} className="row">
            <span className="av" aria-hidden="true">
              {p.name.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <b>{p.name}</b>
              <p className="muted">
                {i === 0 ? "First" : `If ${first?.name ?? "they"} doesn't answer in 10 min`}
                {p.id === myId && me.status === "ok"
                  ? here
                    ? " · passkey on this device"
                    : me.data.hasPasskey
                      ? " · passkey on another device"
                      : " · no passkey yet"
                  : ""}
              </p>
            </div>
            {p.id === myId && me.status === "ok" ? (
              // Always offered: people approve from several devices, and a passkey lives on one device.
              <button
                type="button"
                className={`btn sm${here ? " alt" : ""}`}
                onClick={savePasskey}
                disabled={savingKey}
              >
                {savingKey ? "Waiting…" : here ? "Add another device" : "Add one for this device"}
              </button>
            ) : (
              <span className={`st${p.id === myId || p.role === "owner" ? " ok" : ""}`}>
                {p.id === myId || p.role === "owner" ? "Ready" : "Invite sent"}
              </span>
            )}
          </li>
        ))}
        <li className="row">
          <span className="plus" aria-hidden="true">
            +
          </span>
          {adding ? (
            <form onSubmit={invite} aria-label="Add someone" style={{ gridColumn: "2 / 4" }}>
              <label className="label" htmlFor="inv-name">
                Name
              </label>
              <input id="inv-name" className="field" required value={name} onChange={(e) => setName(e.target.value)} />
              <label className="label" htmlFor="inv-email">
                Email
              </label>
              <input
                id="inv-email"
                className="field"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div className="btn-row" style={{ margin: 0 }}>
                <button type="submit" className="btn sm">
                  Send invite
                </button>
                <button type="button" className="btn alt sm" onClick={() => setAdding(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button type="button" className="link-btn" style={{ justifySelf: "start" }} onClick={() => setAdding(true)}>
              Add someone
            </button>
          )}
        </li>
      </ul>
      {err && <ErrorNote error={err} />}
    </div>
  );
}

export function ChannelList() {
  const { api } = useDeps();
  const channels = useResource((a) => a.channels());
  const [err, setErr] = useState<Error | null>(null);
  if (channels.status === "loading") return <Loading what="Loading channels" />;
  if (channels.status === "error") return <ErrorNote error={channels.error} retry={channels.reload} />;

  async function toggle(choice: (typeof CHANNEL_CHOICES)[number], on: boolean) {
    if (channels.status !== "ok") return;
    setErr(null);
    const matching = channels.data.filter((c) => choice.kinds.includes(c.kind));
    try {
      for (const c of matching) {
        // Turning a channel off keeps its setup; it just stops receiving alerts.
        const levels: Channel["levels"] = on ? defaultLevels(c.kind) : [];
        await api.saveChannel({ ...c, levels });
      }
      channels.reload();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
    }
  }

  return (
    <div>
      <ul className="box list">
        {CHANNEL_CHOICES.map((choice) => {
          const matching = channels.data.filter((c) => choice.kinds.includes(c.kind));
          const on = matching.some(channelOn);
          const id = `ch-${choice.kinds[0]}`;
          return (
            <li key={choice.title} className="row" style={{ gridTemplateColumns: "1fr" }}>
              <label className="check" htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={on}
                  disabled={matching.length === 0}
                  onChange={(e) => void toggle(choice, e.target.checked)}
                />
                <span>
                  <b>{choice.title}</b>
                  <span className="muted" style={{ display: "block" }}>
                    {matching.length === 0 ? "Not set up yet" : choice.detail}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {err && <ErrorNote error={err} />}
    </div>
  );
}

const PRESET_CARDS: Array<{ id: PolicyPreset; title: string; body: string; rec?: boolean }> = [
  { id: "careful", title: "Careful", body: "Ask me before every fix." },
  {
    id: "balanced",
    title: "Balanced",
    body: "Restart and add capacity alone. Ask before undoing an update or touching payments.",
    rec: true,
  },
  { id: "handsoff", title: "Hands-off", body: "Fix anything that can be undone. Tell me after." },
];

type RuleLike = { kind: string; mode: string; when?: { floor?: readonly string[] } };

/**
 * Which preset the current rules equal, if any. A rule scoped to a floor signal that says the same as the
 * kind's general rule (or the default "ask") changes nothing, so it doesn't break the match: switching a
 * row away and back again still reads as the preset (bug 14).
 */
export function presetOf(rules: ReadonlyArray<RuleLike>): PolicyPreset | null {
  const general = (rs: ReadonlyArray<RuleLike>, kind: string) =>
    rs.find((r) => r.kind === kind && !r.when?.floor)?.mode ?? "ask";
  const key = (rs: ReadonlyArray<RuleLike>) =>
    [
      ...new Set(
        rs
          .filter((r) => !(r.when?.floor && r.mode === general(rs, r.kind)))
          .map((r) => `${r.kind}:${r.when?.floor ? `${[...r.when.floor].sort().join("+")}:` : ""}${r.mode}`),
      ),
    ]
      .sort()
      .join(",");
  const k = key(rules);
  return (Object.keys(PRESETS) as PolicyPreset[]).find((p) => key(PRESETS[p]) === k) ?? null;
}

export function SetupHelper() {
  const { api } = useDeps();
  const navigate = useNavigate();
  const [choice, setChoice] = useState<PolicyPreset>("balanced");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  async function finish() {
    setBusy(true);
    setErr(null);
    try {
      await api.applyPreset(choice);
      navigate("/home");
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
      setBusy(false);
    }
  }

  return (
    <SetupFrame step={3} title="How much can your helper do without asking?" pager={setupHelperPager(choice)}>
      <div className="picks" role="radiogroup" aria-label="How much can your helper do without asking?">
        {PRESET_CARDS.map((p) => (
          // biome-ignore lint/a11y/useSemanticElements: card-style radio; native inputs can't hold this layout.
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={choice === p.id}
            className="box pick"
            onClick={() => setChoice(p.id)}
            onKeyDown={(e) => {
              const i = PRESET_CARDS.findIndex((x) => x.id === choice);
              const d =
                e.key === "ArrowRight" || e.key === "ArrowDown"
                  ? 1
                  : e.key === "ArrowLeft" || e.key === "ArrowUp"
                    ? -1
                    : 0;
              if (!d) return;
              e.preventDefault();
              const next = PRESET_CARDS[(i + d + PRESET_CARDS.length) % PRESET_CARDS.length];
              if (next) {
                setChoice(next.id);
                const el = e.currentTarget.parentElement?.querySelector<HTMLButtonElement>(
                  `[data-preset="${next.id}"]`,
                );
                el?.focus();
              }
            }}
            tabIndex={choice === p.id ? 0 : -1}
            data-preset={p.id}
          >
            <b>{p.title}</b>
            {p.rec && <span className="rec">Most people pick this</span>}
            <p>{p.body}</p>
          </button>
        ))}
      </div>
      <p className="note" style={{ marginTop: 14 }}>
        Change it per fix later. Only you can.
      </p>
      {err && <ErrorNote error={err} />}
      <div className="btn-row">
        <button type="button" className="btn alt" onClick={() => navigate("/setup/people")}>
          Back
        </button>
        <button type="button" className="btn" onClick={finish} disabled={busy}>
          {busy ? "Saving…" : "Finish, go to my dashboard"}
        </button>
      </div>
    </SetupFrame>
  );
}
